import { analyzeBreakpoints, Dict } from '../../utils';
import type { WithCSSVar } from '../utils/types';
import { createThemeVars } from './create-theme-vars';
import { extractTokens, omitVars } from './theme-tokens';

export function toCSSVar<T extends Dict>(rawTheme: T) {
  /**
   * In the case the theme has already been converted to css-var (e.g extending the theme),
   * we can omit the computed css vars and recompute it for the extended theme.
   */
  const theme = omitVars(rawTheme);

  // omit components and breakpoints from css variable map
  const tokens = extractTokens(theme);

  const cssVarPrefix = theme.config?.cssVarPrefix;

  const {
    /**
     * This is more like a dictionary of tokens users will type `green.500`,
     * and their equivalent css variable.
     */
    cssMap,
    /**
     * The extracted css variables will be stored here, and used in
     * the emotion's <Global/> component to attach variables to `:root`
     */
    cssVars,
  } = createThemeVars(tokens, { cssVarPrefix });

  const defaultCssVars: Dict = {
    '--potion-ring-inset': 'var(--potion-empty,/*!*/ /*!*/)',
    '--potion-ring-offset-width': '0px',
    '--potion-ring-offset-color': '#fff',
    '--potion-ring-color': 'rgba(66, 153, 225, 0.6)',
    '--potion-ring-offset-shadow': '0 0 #0000',
    '--potion-ring-shadow': '0 0 #0000',
    '--potion-space-x-reverse': '0',
    '--potion-space-y-reverse': '0',
  };

  Object.assign(theme, {
    __cssVars: { ...defaultCssVars, ...cssVars },
    __cssMap: cssMap,
    __breakpoints: analyzeBreakpoints(theme.breakpoints),
  });

  return theme as WithCSSVar<T>;
}
